var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { SubmissionError } from 'redux-form';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { apiCreateProffitSearchRegistration } from '../../../../actions/ProffitSearchRegistrationAction';
import { apiGetOneProfile, apiUpdateProfileNameAndPosition, } from '../../../../actions/ProfileActions';
import LoginButton from '../../../../container/Ui/Login/LoginButton';
import { PROFESSIONAL_POSITION_CANDIDATES } from '../../../../define/consts/profile';
import { REQUEST_DIVISION_CANDIDATE_PRO } from '../../../../define/consts/registration';
import { BAD_DIVISION_CLIENT, BAD_DIVISION_PROFESSIONAL, BAD_REGISTRATION_INVALID_EMAIL, } from '../../../../define/response';
import { wrapNotifyError, wrapDispatch, notify } from '../../../action';
import { getProffitSearchValues } from '../../../profile';
import { createValidateErrorData, hasReduxFormError } from '../../../validate';
import useAnalytics from '../../analytics/useAnalytics';
import { useBoolState } from '../../useHook';
import { useKanaRef } from '../../useKana';
/**
 * Proffit Searchへのトライアル登録申請を制御するuse
 * @param profile
 * @param userMode
 * @param dispatch
 * @param unmountRef
 * @returns {*}
 */
export const useSlideSearchRegistrationModal = (profile, userMode, dispatch, unmountRef) => {
    const history = useNavigate();
    const { boolState: isShowFormModal, changeFalse: hideFormModal, changeTrue: rawShowFormModal, } = useBoolState(false);
    const { boolState: isShowCompleteModal, changeFalse: hideCompleteModal, changeTrue: showCompleteModal, } = useBoolState(false);
    const { onClickLandingPageModalLink, onClickRegistration } = useAnalytics.useProffitSearch();
    const [email, changeEmail] = useState('');
    const kanaRef = useKanaRef();
    const { userAllowDeviceId } = useMemo(() => getProffitSearchValues(profile), [profile]);
    const isGuest = userMode === 'guest';
    const isProfessional = userMode === 'professional';
    /**
     * 登録モーダルを表示
     * @type {(function(): void)|*}
     */
    const showFormModal = useCallback(() => {
        // GAイベントの発火
        onClickLandingPageModalLink();
        rawShowFormModal();
    }, [rawShowFormModal, onClickLandingPageModalLink]);
    /**
     * フォームの初期値
     * @type {*}
     */
    const initialValues = useMemo(() => {
        if (isGuest || !profile) {
            return undefined;
        }
        // profileの値で初期値設定
        const { firstName, lastName, position, positionOther } = profile.information;
        const values = {
            firstName,
            lastName,
        };
        // コンサルの場合は役職を設定
        if (isProfessional) {
            values.position =
                PROFESSIONAL_POSITION_CANDIDATES.find((v) => v.value === parseInt(position, 10)) || {};
            values.positionOther = positionOther || '';
        }
        return values;
    }, [profile, isGuest, isProfessional]);
    /**
     * submit時のイベント（新規登録）
     * @type {(function(*): Promise<void>)|*}
     */
    const onSubmit = useCallback((formValues) => __awaiter(void 0, void 0, void 0, function* () {
        // GAイベントの発火
        onClickRegistration();
        const params = {
            firstName: formValues.firstName,
            firstNameKana: kanaRef.current.first.getFurigana(),
            lastName: formValues.lastName,
            lastNameKana: kanaRef.current.last.getFurigana(),
        };
        if (isGuest) {
            params.email = formValues.email;
            params.requestDivision = parseInt(formValues.requestDivision, 10);
        }
        else {
            params.email = profile.email;
            params.firstNameKana =
                params.firstNameKana || profile.information.firstNameKana;
            params.lastNameKana =
                params.lastNameKana || profile.information.lastNameKana;
        }
        // コンサル会員の場合
        if (isProfessional ||
            params.requestDivision === REQUEST_DIVISION_CANDIDATE_PRO.value) {
            params.position = parseInt(formValues.position.value, 10);
            if (formValues.position.other) {
                params.positionOther = formValues.positionOther;
            }
        }
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                yield wrapDispatch(dispatch, apiCreateProffitSearchRegistration(params));
                // 会員の場合はプロフィールを最新の状態に更新
                if (!isGuest) {
                    yield wrapDispatch(dispatch, apiGetOneProfile());
                }
                if (unmountRef.current) {
                    return;
                }
                hideFormModal();
                if (userAllowDeviceId) {
                    // 端末登録済みユーザーの場合
                    history.push('/proffit_search');
                    notify('無料トライアルのご登録が完了しました');
                }
                else {
                    // 完了モーダルを表示
                    changeEmail(params.email);
                    showCompleteModal();
                }
            }
            catch (e) {
                if (unmountRef.current) {
                    return;
                }
                const emailError = {
                    key: 'email',
                    message: 'このメールアドレスはご利用できません',
                };
                const errorMessage = createValidateErrorData(e, {
                    [BAD_DIVISION_CLIENT]: emailError,
                    [BAD_DIVISION_PROFESSIONAL]: emailError,
                    [BAD_REGISTRATION_INVALID_EMAIL]: {
                        key: 'email',
                        message: (_jsxs(_Fragment, { children: ["\u767B\u9332\u6E08\u307F\u306E\u30A2\u30C9\u30EC\u30B9\u306E\u305F\u3081\u3001\u5148\u305A", _jsx(LoginButton, { className: 'link-text', text: 'こちらからログイン' }), "\u3057\u3066\u304F\u3060\u3055\u3044\u3002"] })),
                    },
                });
                if (hasReduxFormError(errorMessage)) {
                    throw new SubmissionError(errorMessage);
                }
                else {
                    throw e;
                }
            }
        }));
    }), [
        changeEmail,
        dispatch,
        hideFormModal,
        isGuest,
        isProfessional,
        kanaRef,
        onClickRegistration,
        profile,
        showCompleteModal,
        unmountRef,
        userAllowDeviceId,
        history,
    ]);
    /**
     * submit時のイベント（氏名・役職のみ更新）
     * @type {(function(*): Promise<void>)|*}
     */
    const onSubmitOnlyNameAndPosition = useCallback((formValues) => __awaiter(void 0, void 0, void 0, function* () {
        const params = {
            firstName: formValues.firstName,
            firstNameKana: kanaRef.current.first.getFurigana(),
            lastName: formValues.lastName,
            lastNameKana: kanaRef.current.last.getFurigana(),
        };
        // コンサル会員の場合は役職を設定する
        if (isProfessional) {
            params.position = parseInt(formValues.position.value, 10);
            if (formValues.position.other) {
                params.positionOther = formValues.positionOther;
            }
        }
        // 更新を実行
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            // 氏名や役職のみ更新
            yield wrapDispatch(dispatch, apiUpdateProfileNameAndPosition(params));
            // プロフィールを最新の状態に更新
            yield wrapDispatch(dispatch, apiGetOneProfile());
        }));
    }), [dispatch, isProfessional, kanaRef]);
    return {
        email,
        hideCompleteModal,
        hideFormModal,
        initialValues,
        isShowCompleteModal,
        isShowFormModal,
        kanaRef,
        onSubmit,
        onSubmitOnlyNameAndPosition,
        showCompleteModal,
        showFormModal,
    };
};
export const useSlideSearchRegistration = {
    useSlideSearchRegistrationModal,
};
